import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/staff";
import {
  Radio,
  Bot,
  Users,
  ShieldAlert,
  Plus,
  RefreshCw,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Key,
  Eye,
  EyeOff,
  RotateCw,
} from "lucide-react";
import { Badge } from "../components/common/Badge";
import { Modal } from "../components/common/Modal";

interface TokenItem {
  id: string;
  token: string;
  label: string | null;
  isValid: boolean;
  createdAt: string;
}

export function DiscordTracker() {
  const [tokens, setTokens] = useState<TokenItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [tokenInput, setTokenInput] = useState("");
  const [labelInput, setLabelInput] = useState("Surveillance Discord (OSINT)");
  const [showToken, setShowToken] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // Row action states
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  function loadTokens() {
    setLoading(true);
    api<{ tokens: TokenItem[] }>("/staff/managers/discord/tokens")
      .then((res) => {
        setTokens(res.tokens || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Erreur lors du chargement des tokens");
        setLoading(false);
      });
  }

  useEffect(() => {
    loadTokens();
  }, []);

  async function handleAddToken(e: React.FormEvent) {
    e.preventDefault();
    if (!tokenInput.trim()) {
      setModalError("Veuillez saisir un token Discord valide");
      return;
    }

    setSubmitting(true);
    setModalError(null);

    try {
      const res = await api<{ success: boolean; token: TokenItem }>("/staff/managers/discord/tokens", {
        method: "POST",
        body: JSON.stringify({
          token: tokenInput.trim(),
          label: labelInput.trim() || "Surveillance Discord (OSINT)",
        }),
      });

      setSubmitting(false);
      setModalOpen(false);
      setTokenInput("");
      setLabelInput("Surveillance Discord (OSINT)");

      if (res.success) {
        setSuccessMsg("Bot Discord connecté avec succès et actif dans le cluster !");
      } else {
        setSuccessMsg("Token enregistré, mais la connexion a échoué (vérifiez la validité du token).");
      }

      setTimeout(() => setSuccessMsg(null), 6000);
      loadTokens();
    } catch (err: any) {
      setSubmitting(false);
      setModalError(err.message || "Erreur lors de l'ajout du token");
    }
  }

  async function handleDelete(id: string) {
    setActionLoadingId(id);
    try {
      await api<{ success: boolean }>(`/staff/managers/discord/tokens/${id}`, {
        method: "DELETE",
      });
      setDeleteConfirmId(null);
      setActionLoadingId(null);
      setSuccessMsg("Compte marionnette supprimé.");
      setTimeout(() => setSuccessMsg(null), 4000);
      loadTokens();
    } catch (err: any) {
      setActionLoadingId(null);
      setError(err.message || "Erreur lors de la suppression");
    }
  }

  async function handleRestart(id: string) {
    setActionLoadingId(id);
    try {
      const res = await api<{ success: boolean }>(`/staff/managers/discord/tokens/${id}/restart`, {
        method: "POST",
      });
      setActionLoadingId(null);
      if (res.success) {
        setSuccessMsg("Connexion du bot relancée avec succès !");
      } else {
        setError("Échec de la reconnexion du bot.");
      }
      setTimeout(() => {
        setSuccessMsg(null);
        setError(null);
      }, 5000);
      loadTokens();
    } catch (err: any) {
      setActionLoadingId(null);
      setError(err.message || "Erreur lors du redémarrage");
    }
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-12">
      {/* Service Keys & Tokens Navigation Tabs */}
      <div className="flex items-center gap-2 border-b border-border pb-3">
        <Link
          to="/service-keys"
          className="px-3.5 py-1.5 rounded-lg text-xs font-semibold text-muted-foreground hover:bg-accent hover:text-foreground transition-colors flex items-center gap-1.5"
        >
          <Key className="w-3.5 h-3.5" />
          Clés API Serveur
        </Link>
        <Link
          to="/discord"
          className="px-3.5 py-1.5 rounded-lg text-xs font-bold bg-primary text-primary-foreground shadow-xs flex items-center gap-1.5"
        >
          <Radio className="w-3.5 h-3.5" />
          Discord Tokens & Tracker
        </Link>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2.5">
            <Radio className="w-6 h-6 text-primary animate-pulse" />
            Surveillance Discord (OSINT)
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Gérez vos bots marionnettes et observez les données d'ingestion en temps réel.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={loadTokens}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-semibold rounded-lg border border-border bg-card text-foreground hover:bg-accent transition-colors disabled:opacity-50"
            title="Rafraîchir la liste"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </button>

          <button
            type="button"
            onClick={() => {
              setModalError(null);
              setModalOpen(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm transition-colors"
          >
            <Plus className="w-4 h-4" />
            Ajouter un bot / token
          </button>
        </div>
      </div>

      {/* Notifications */}
      {successMsg && (
        <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm flex items-center gap-3 animate-in fade-in">
          <CheckCircle2 className="w-5 h-5 shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      {error && (
        <div className="p-4 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-sm flex items-center gap-3 animate-in fade-in">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-5 rounded-xl bg-card border border-border shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Bots Connectés
            </span>
            <Bot className="w-5 h-5 text-primary" />
          </div>
          <div className="text-2xl font-bold text-foreground mt-2">
            {tokens.filter((t) => t.isValid).length} / {tokens.length}
          </div>
          <p className="text-xs text-muted-foreground mt-1">Actifs dans le Master/Worker</p>
        </div>

        <div className="p-5 rounded-xl bg-card border border-border shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Architecture
            </span>
            <ShieldAlert className="w-5 h-5 text-emerald-500" />
          </div>
          <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-2">
            Stateless
          </div>
          <p className="text-xs text-muted-foreground mt-1">Déduplication Redis 10s</p>
        </div>

        <div className="p-5 rounded-xl bg-card border border-border shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Mode d'Ingestion
            </span>
            <Users className="w-5 h-5 text-blue-500" />
          </div>
          <div className="text-2xl font-bold text-foreground mt-2">Auto-Discovery</div>
          <p className="text-xs text-muted-foreground mt-1">Capture continue 24h/24</p>
        </div>
      </div>

      {/* Tokens List */}
      <div className="bg-card border border-border rounded-xl overflow-hidden shadow-xs">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-foreground">Comptes Marionnettes (Tokens)</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Ces comptes capturent automatiquement les messages, vocaux, avatars et présences Discord.
            </p>
          </div>

          <button
            type="button"
            onClick={() => {
              setModalError(null);
              setModalOpen(true);
            }}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Nouveau token
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center text-sm text-muted-foreground flex flex-col items-center justify-center gap-2">
            <RefreshCw className="w-5 h-5 animate-spin text-primary" />
            Chargement des comptes marionnettes…
          </div>
        ) : tokens.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground flex flex-col items-center justify-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
              <Key className="w-6 h-6" />
            </div>
            <div>
              <p className="font-medium text-foreground text-base">Aucun compte marionnette configuré</p>
              <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
                Ajoutez un token Discord pour démarrer la capture et la surveillance en temps réel.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setModalError(null);
                setModalOpen(true);
              }}
              className="mt-2 inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Ajouter votre premier bot
            </button>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {tokens.map((t) => (
              <div key={t.id} className="p-4 sm:px-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3.5">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold shrink-0 ${
                    t.isValid ? "bg-primary/15 text-primary ring-1 ring-primary/20" : "bg-destructive/15 text-destructive ring-1 ring-destructive/20"
                  }`}>
                    <Bot className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm text-foreground flex items-center gap-2">
                      {t.label || "Compte Discord"}
                      <span className="text-[11px] text-muted-foreground font-normal">
                        ({new Date(t.createdAt).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })})
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground font-mono mt-0.5 tracking-wider">{t.token}</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 self-end sm:self-center">
                  <Badge variant={t.isValid ? "success" : "destructive"}>
                    {t.isValid ? "En ligne" : "Invalide / Banni"}
                  </Badge>

                  {/* Reconnect button */}
                  <button
                    type="button"
                    onClick={() => handleRestart(t.id)}
                    disabled={actionLoadingId === t.id}
                    title="Relancer la connexion du bot"
                    className="p-1.5 rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-accent transition-colors disabled:opacity-40"
                  >
                    <RotateCw className={`w-4 h-4 ${actionLoadingId === t.id ? "animate-spin" : ""}`} />
                  </button>

                  {/* Delete button */}
                  {deleteConfirmId === t.id ? (
                    <div className="flex items-center gap-1.5 animate-in fade-in">
                      <button
                        type="button"
                        onClick={() => handleDelete(t.id)}
                        disabled={actionLoadingId === t.id}
                        className="px-2.5 py-1 text-xs font-semibold rounded-md bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
                      >
                        Confirmer
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteConfirmId(null)}
                        className="px-2 py-1 text-xs font-medium rounded-md border border-border hover:bg-accent transition-colors"
                      >
                        Annuler
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setDeleteConfirmId(t.id)}
                      title="Supprimer ce token"
                      className="p-1.5 rounded-lg border border-border bg-card text-muted-foreground hover:text-destructive hover:bg-destructive/10 hover:border-destructive/30 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Ajouter un token */}
      <Modal
        open={modalOpen}
        onClose={() => !submitting && setModalOpen(false)}
        title="Ajouter un compte marionnette"
        description="Le compte se connectera instantanément et écoutera les flux en tâche de fond."
      >
        <form onSubmit={handleAddToken} className="space-y-4 pt-2">
          {modalError && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{modalError}</span>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5">
              Nom / Libellé du compte
            </label>
            <input
              type="text"
              value={labelInput}
              onChange={(e) => setLabelInput(e.target.value)}
              placeholder="Ex: Surveillance Discord (OSINT)"
              className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5">
              Token Discord
            </label>
            <div className="relative">
              <input
                type={showToken ? "text" : "password"}
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                placeholder="Ex: MTUzODM1NDUwMzExNDMwMTQ2Mg..."
                spellCheck={false}
                autoComplete="off"
                className="w-full px-3 py-2 pr-10 text-sm font-mono rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
              />
              <button
                type="button"
                onClick={() => setShowToken(!showToken)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-0.5"
              >
                {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">
              Accepte les bot tokens ou user tokens. La connexion est testée dès la soumission.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              disabled={submitting}
              className="px-4 py-2 text-xs font-semibold rounded-lg border border-border bg-card text-foreground hover:bg-accent transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting || !tokenInput.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  Connexion en cours…
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Ajouter le bot
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
